import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Cloud, Database, Shield, Cog, CheckCircle2, TrendingUp, Clock, HelpCircle, ChevronDown, Server } from 'lucide-react';
import SEOHead from '../../components/SEOHead';

const keyMetrics = [
  {
    icon: CheckCircle2,
    value: 'Zero',
    label: 'Data Loss',
    description: 'Across every production migration we have delivered'
  },
  {
    icon: TrendingUp,
    value: '40%',
    label: 'Cost Reduction',
    description: 'Average infrastructure cost saving post-migration'
  },
  {
    icon: Clock,
    value: '99.9%',
    label: 'Uptime',
    description: 'Post-cutover across all migrated production environments'
  },
  {
    icon: TrendingUp,
    value: '25%',
    label: 'Faster Processing',
    description: 'Average ETL and pipeline performance improvement post-migration'
  }
];

const services = [
  {
    title: 'Database Migration',
    icon: Database,
    items: [
      'On-Premises to Azure SQL Managed Instance: Full T-SQL compatibility migration with schema validation and parallel testing',
      'Legacy SQL Server to Cloud: Assessment, field mapping correction, data type alignment, and cutover management',
      'ETL Pipeline Migration: SSIS to Azure Data Factory with automated orchestration and self-hosted integration runtimes',
      'Analytics Migration: SSAS cubes to direct table extracts eliminating cube overhead in cloud environments'
    ]
  },
  {
    title: 'Cloud Infrastructure & IaC',
    icon: Cloud,
    items: [
      'Terraform Infrastructure as Code: Version-controlled, repeatable infrastructure deployment across AWS and Azure',
      'CI/CD Pipeline Setup: GitLab and GitHub Actions pipelines for automated, auditable infrastructure deployments',
      'Multi-Cloud Architecture: AWS and Azure configurations optimised for workload-specific placement and cost efficiency',
      'Container Orchestration: Docker and Kubernetes configuration for scalable, portable production deployments'
    ]
  },
  {
    title: 'Data Platform Modernisation',
    icon: Server,
    items: [
      'Azure Data Lake Storage: Scalable raw and staging data tiers replacing on-premises file and database storage',
      'AWS Data Platform: Lambda, Glue, Athena, EventBridge, and CloudWatch deployed as a unified monitored platform',
      'Cloud Data Warehouse: Snowflake, Redshift, and BigQuery implementations replacing legacy analytical infrastructure',
      'Real-Time Data Integration: Event-driven architectures using Azure Service Bus, AWS EventBridge, and Apache Kafka'
    ]
  },
  {
    title: 'Security, Governance & Automation',
    icon: Shield,
    items: [
      'Identity & Access Management: Azure AD, Key Vault, private endpoints, and role-based access controls',
      'Row-Level Security: Multi-tenant data access controls in Tableau and Power BI for complex security requirements',
      'Automated Report Delivery: Power Automate and Azure Logic Apps replacing legacy SSRS subscription workflows',
      'Compliance & Audit Logging: Full audit trail implementation for data access, pipeline execution, and infrastructure changes'
    ]
  }
];

const techStack = [
  {
    category: 'Cloud Platforms',
    items: [
      { name: 'Microsoft Azure', logo: '/tech-logos/azure-logo.svg' },
      { name: 'Amazon Web Services', logo: '/tech-logos/aws.svg' },
      { name: 'Google Cloud Platform', logo: '/tech-logos/google-cloud.svg' }
    ]
  },
  {
    category: 'Data Integration',
    items: [
      { name: 'Azure Data Factory', logo: '/tech-logos/azure-logo.svg' },
      { name: 'AWS Glue', logo: '/tech-logos/aws.svg' },
      { name: 'Apache Airflow', logo: '/tech-logos/airflow.svg' }
    ]
  },
  {
    category: 'Infrastructure',
    items: [
      { name: 'Terraform', logo: '/tech-logos/terraform.svg' },
      { name: 'Docker', logo: '/tech-logos/docker.svg' },
      { name: 'Kubernetes', logo: '/tech-logos/kubernetes.svg' }
    ]
  },
  {
    category: 'Storage & Compute',
    items: [
      { name: 'Azure SQL Managed Instance', logo: '/tech-logos/azure-logo.svg' },
      { name: 'Azure Data Lake Storage', logo: '/tech-logos/azure-logo.svg' },
      { name: 'Amazon Redshift', logo: '/tech-logos/aws.svg' }
    ]
  }
];

const industries = [
  {
    title: 'FMCG & Manufacturing',
    icon: Cog,
    applications: [
      'Full on-premises to Azure migrations',
      'ETL and reporting infrastructure modernisation',
      'Multi-tenant security and access management'
    ]
  },
  {
    title: 'Healthcare',
    icon: Shield,
    applications: [
      'HIPAA-compliant cloud architecture',
      'EMR system data integration pipelines',
      'Clinical data warehouse implementation'
    ]
  },
  {
    title: 'Technology & Consulting',
    icon: Cloud,
    applications: [
      'Multi-service AWS platform automation',
      'Terraform IaC for enterprise service portfolios',
      'Long-term infrastructure managed delivery'
    ]
  },
  {
    title: 'Elevator & Field Services',
    icon: Database,
    applications: [
      'Legacy CHAMP database to Azure SQL migration',
      'Schema validation and referential integrity management',
      'Field service data modernisation'
    ]
  }
];

const successStories = [
  {
    tag: 'FMCG',
    title: 'FMCG Enterprise — Full Azure Migration',
    metrics: [
      '200+ stored procedures migrated with 98%+ success rate',
      '1,000+ SSRS reports converted to Power BI Paginated Reports',
      '40% infrastructure cost reduction, 99.9% uptime, zero data loss',
      'Delivered in 20 weeks'
    ],
    link: '/case-studies/fmcg-migration'
  },
  {
    tag: 'Technology & Consulting',
    title: 'Enterprise Technology — AWS & Azure Cloud Platform',
    metrics: [
      'Legacy database migrated to Azure SQL with zero data loss',
      '15+ enterprise services automated on unified AWS platform',
      'Full Terraform IaC deployed across all services',
      '17+ month sustained engagement'
    ],
    link: '/case-studies/cloud-platform-migration'
  }
];

const methodology = [
  {
    title: 'Infrastructure Discovery & Assessment',
    description: 'Full inventory of on-premises components, dependencies, and migration risk mapping',
    icon: Database
  },
  {
    title: 'Migration Architecture Design',
    description: 'Cloud platform selection, schema mapping, security design, and phased cutover planning',
    icon: Cloud
  },
  {
    title: 'Environment Setup & Parallel Build',
    description: 'Cloud environment provisioned and tested in parallel with live on-premises systems',
    icon: Server
  },
  {
    title: 'Phased Migration Execution',
    description: 'Batch-by-batch migration with validation gates and rollback capability at every stage',
    icon: Cog
  },
  {
    title: 'Security & Governance Implementation',
    description: 'IAM, Key Vault, private endpoints, audit logging, and RLS configuration',
    icon: Shield
  },
  {
    title: 'Cutover, Validation & Hypercare',
    description: 'Production cutover with hypercare support period, performance monitoring, and optimisation',
    icon: CheckCircle2
  }
];

const whyChooseUs = [
  {
    title: 'Zero Data Loss Track Record',
    description: 'Every production migration we have delivered has been completed without data loss through phased validation, parallel testing, and transaction management.',
    icon: CheckCircle2
  },
  {
    title: 'Full Stack Migration Capability',
    description: 'We handle the complete migration stack — database, ETL, analytics, reporting, security, and delivery automation — without splitting the engagement across multiple vendors.',
    icon: Cloud
  },
  {
    title: 'IaC-First Approach',
    description: 'We build infrastructure as code from day one meaning every environment is reproducible, version-controlled, and auditable — not a collection of manually provisioned resources.',
    icon: Server
  },
  {
    title: 'Business Continuity Focus',
    description: 'Our migrations are designed around zero disruption. Parallel environments, phased cutovers, and rollback plans are built into every engagement before a single object is moved.',
    icon: Shield
  }
];

const faqs = [
  {
    question: 'How do you ensure zero data loss during migration?',
    answer: 'Through phased batch migration with schema validation gates before each batch commits, parallel run validation comparing cloud output to on-premises source, and transaction management with automatic rollback on any failure.'
  },
  {
    question: 'How long does a full cloud migration take?',
    answer: 'Scope-dependent. A focused database and ETL migration runs 8–12 weeks. A full enterprise migration including reporting infrastructure runs 16–20 weeks. We provide a fixed-scope assessment before committing to any timeline.'
  },
  {
    question: 'Can you migrate our legacy SSIS packages to Azure Data Factory?',
    answer: 'Yes. SSIS to ADF migration is a core competency. We handle complex transformations, parameterisation, and self-hosted integration runtime setup for on-premises source connectivity.'
  },
  {
    question: 'Do you continue to support the platform post-migration?',
    answer: 'Yes. Post-migration support and ongoing managed delivery are available. Several clients engage us for 12+ month periods following initial migration for optimisation and platform expansion.'
  }
];

const CloudMigrationSolution = () => {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  return (
    <>
      <SEOHead
        title="Cloud Migration & Infrastructure Solutions | Agility.AI"
        description="Move legacy on-premises data infrastructure to modern cloud platforms with zero data loss, maintained business continuity, and measurable cost reduction."
        keywords="cloud migration, Azure migration, AWS migration, infrastructure as code, Terraform, database migration, SSIS to ADF"
        url="https://www.agilitytech.ai/solutions/cloud-migration"
      />
      <div className="pt-20">
        {/* Hero Section */}
        <section className="relative py-24 bg-gradient-to-r from-blue-900 to-black overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img
              src="https://images.unsplash.com/photo-1451187580459-43490279c0fa?auto=format&fit=crop&q=80"
              alt="Cloud Migration Banner"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
          </div>
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-2 bg-teal-500/10 rounded-full border border-teal-500/20 mb-6">
                <span className="text-teal-400 text-sm font-medium">Cloud Migration & Infrastructure</span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Cloud Migration & Infrastructure Solutions
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed mb-8 max-w-3xl">
                Move legacy on-premises data infrastructure to modern cloud platforms with zero data loss,
                maintained business continuity, and measurable cost reduction — delivered by engineers
                who have done it at enterprise scale.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="bg-teal-500 text-white px-8 py-4 rounded-lg text-lg font-medium
                    hover:bg-teal-400 transition-all duration-300 inline-flex items-center space-x-2
                    shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                >
                  <span>Schedule Your Cloud Assessment</span>
                  <ArrowRight className="h-5 w-5" />
                </Link>
                <button
                  className="bg-white/10 text-white px-8 py-4 rounded-lg text-lg font-medium
                    hover:bg-white/20 transition-all duration-300 inline-flex items-center space-x-2
                    border border-white/20 hover:border-white/30"
                >
                  <span>Download Migration Guide</span>
                  <ArrowRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Key Metrics Section */}
        <section className="py-16 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {keyMetrics.map((metric, index) => (
                <div key={index} className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6
                  border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300 text-center">
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <metric.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                  <div className="text-teal-400 font-medium mb-2">{metric.label}</div>
                  <p className="text-gray-400 text-sm">{metric.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Migrate & Build Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              What We Migrate & Build
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {services.map((service, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                    transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center
                    justify-center mb-6">
                    <service.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{service.title}</h3>
                  <ul className="space-y-3">
                    {service.items.map((item, idx) => (
                      <li key={idx} className="flex items-start space-x-3 text-gray-300">
                        <CheckCircle2 className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                        <span className="text-sm leading-relaxed">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Stack Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Technology Stack
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {techStack.map((category, index) => (
                <div key={index} className="space-y-6">
                  <h3 className="text-xl font-bold text-teal-400 mb-4 text-center">{category.category}</h3>
                  <div className="grid grid-cols-1 gap-4">
                    {category.items.map((item, idx) => (
                      <div
                        key={idx}
                        className="flex items-center space-x-3 bg-gray-800/50 rounded-lg p-4
                          border border-gray-700/50 hover:bg-gray-800/70 transition-all duration-300"
                      >
                        <img
                          src={item.logo}
                          alt={`${item.name} logo`}
                          className="h-8 w-8 object-contain"
                        />
                        <span className="text-gray-300 font-medium">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Industry Applications Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Industry Applications
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {industries.map((industry, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-8
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300
                    transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center
                    justify-center mb-6">
                    <industry.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{industry.title}</h3>
                  <ul className="space-y-3">
                    {industry.applications.map((app, idx) => (
                      <li key={idx} className="flex items-start space-x-3 text-gray-300">
                        <ArrowRight className="h-5 w-5 text-teal-400 flex-shrink-0 mt-1" />
                        <span className="text-sm leading-relaxed">{app}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Success Stories Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Featured Success Stories
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {successStories.map((story, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                    hover:bg-gray-800/70 transition-all duration-300 transform hover:-translate-y-1"
                >
                  <div className="text-sm text-teal-400 font-semibold mb-4">{story.tag}</div>
                  <h3 className="text-xl font-bold text-white mb-6">{story.title}</h3>
                  <div className="space-y-4 mb-6">
                    {story.metrics.map((metric, idx) => (
                      <div key={idx} className="flex items-center space-x-3 text-gray-300">
                        <CheckCircle2 className="h-4 w-4 text-green-400" />
                        <span className="text-sm">{metric}</span>
                      </div>
                    ))}
                  </div>
                  <Link
                    to={story.link}
                    className="inline-flex items-center text-teal-400 hover:text-teal-300
                      transition-colors font-medium"
                  >
                    <span>Read Full Case Study</span>
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Methodology Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Our Methodology
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {methodology.map((step, index) => (
                <div
                  key={index}
                  className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6
                    border border-gray-800/50 hover:bg-gray-900/70 transition-all duration-300"
                >
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-8 h-8 bg-teal-500/10 rounded-full flex items-center
                      justify-center text-teal-400 font-bold">
                      {index + 1}
                    </div>
                    <div className="w-8 h-8 bg-teal-500/10 rounded-lg flex items-center justify-center">
                      <step.icon className="h-5 w-5 text-teal-400" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3">{step.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Agility.AI Section */}
        <section className="py-20 bg-gray-900">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              Why Choose Agility.AI
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs.map((reason, index) => (
                <div
                  key={index}
                  className="bg-gray-800/50 rounded-xl p-8 border border-gray-700/50
                    hover:bg-gray-800/70 transition-all duration-300 text-center"
                >
                  <div className="w-12 h-12 bg-teal-500/10 rounded-xl flex items-center
                    justify-center mb-6 mx-auto">
                    <reason.icon className="h-6 w-6 text-teal-400" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3">{reason.title}</h3>
                  <p className="text-gray-300 text-sm leading-relaxed">{reason.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <HelpCircle className="h-12 w-12 text-teal-400 mx-auto mb-4" />
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  Frequently Asked Questions
                </h2>
                <p className="text-xl text-gray-400">
                  Everything you need to know about our cloud migration process
                </p>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-gray-900/50 backdrop-blur-sm rounded-xl border border-gray-800/50 overflow-hidden"
                  >
                    <button
                      onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                      className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-900/70 transition-all duration-300"
                    >
                      <span className="text-lg font-semibold text-white pr-8">{faq.question}</span>
                      <ChevronDown
                        className={`h-5 w-5 text-teal-400 flex-shrink-0 transition-transform duration-300 ${
                          openFaqIndex === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    {openFaqIndex === index && (
                      <div className="px-6 pb-5">
                        <p className="text-gray-300 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-r from-blue-900 to-black">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="w-16 h-16 bg-teal-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Cloud className="h-8 w-8 text-teal-400" />
              </div>
              <h2 className="text-4xl font-bold text-white mb-6">
                Ready to Move to the Cloud?
              </h2>
              <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                Schedule a cloud assessment with our migration specialists to build a zero-risk
                roadmap for your infrastructure modernisation.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center space-x-3 bg-teal-500 text-white px-8 py-4
                  rounded-lg text-lg font-medium hover:bg-teal-400 transition-all duration-300 group
                  shadow-lg hover:shadow-xl transform hover:-translate-y-1"
              >
                <span>Schedule Your Cloud Assessment</span>
                <ArrowRight className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CloudMigrationSolution;
